import { ethers, BrowserProvider } from 'ethers';

const CLAIM_CONTRACT_ABI = [
  "function claimReward() external",
  "function getClaimableAmount(address user) external view returns (uint256)"
];

/**
 * Check if user has Web3 wallet (MetaMask, etc.)
 */
export const hasWeb3Wallet = (): boolean => {
  return typeof window !== 'undefined' && !!(window as any).ethereum;
};

/**
 * Connect to Web3 wallet
 */
export const connectWallet = async (): Promise<{ success: boolean; address?: string; error?: string }> => {
  try {
    if (!hasWeb3Wallet()) {
      return {
        success: false,
        error: 'No Web3 wallet found. Please install MetaMask.'
      };
    }

    const provider = new BrowserProvider((window as any).ethereum);
    const accounts = await provider.send("eth_requestAccounts", []);
    
    if (accounts.length === 0) {
      return {
        success: false,
        error: 'No account selected'
      };
    }

    return {
      success: true,
      address: accounts[0]
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to connect wallet'
    };
  }
};

/**
 * Switch network to required chain
 */
export const switchNetwork = async (chainId: number): Promise<{ success: boolean; error?: string }> => {
  try {
    const hexChainId = '0x' + chainId.toString(16);
    
    await (window as any).ethereum.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: hexChainId }],
    });

    return { success: true };
  } catch (error: any) {
    // Chain not added, try to add it
    if (error.code === 4902 && chainId === 11155111) {
      try {
        await (window as any).ethereum.request({
          method: 'wallet_addEthereumChain',
          params: [{
            chainId: '0xaa36a7',
            chainName: 'Sepolia Testnet',
            nativeCurrency: {
              name: 'Sepolia ETH',
              symbol: 'ETH',
              decimals: 18
            },
            rpcUrls: ['https://sepolia.drpc.org'],
            blockExplorerUrls: ['https://sepolia.etherscan.io']
          }],
        });
        return { success: true };
      } catch (addError: any) {
        return {
          success: false,
          error: addError.message || 'Failed to add network'
        };
      }
    }
    
    return {
      success: false,
      error: error.message || 'Failed to switch network'
    };
  }
};

/**
 * Get claimable amount from contract
 */
export const getClaimableAmount = async (
  contractAddress: string,
  userAddress: string,
  rpcUrl: string
): Promise<{ success: boolean; amount?: number; error?: string }> => {
  try {
    const provider = new ethers.JsonRpcProvider(rpcUrl);
    const contract = new ethers.Contract(contractAddress, CLAIM_CONTRACT_ABI, provider);
    
    const amountWei = await contract.getClaimableAmount(userAddress);
    const amount = parseFloat(ethers.formatUnits(amountWei, 6));
    
    return {
      success: true,
      amount
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Claim reward from contract
 */
export const claimFromContract = async (
  contractAddress: string,
  chainId: number
): Promise<{ success: boolean; txHash?: string; error?: string }> => {
  try {
    if (!hasWeb3Wallet()) {
      return {
        success: false,
        error: 'No Web3 wallet found. Please install MetaMask.'
      };
    }

    // Connect to wallet
    const provider = new BrowserProvider((window as any).ethereum);
    const signer = await provider.getSigner();
    const userAddress = await signer.getAddress();

    // Check network
    const network = await provider.getNetwork();
    if (Number(network.chainId) !== chainId) {
      const switchResult = await switchNetwork(chainId);
      if (!switchResult.success) {
        return {
          success: false,
          error: `Please switch to ${chainId === 11155111 ? 'Sepolia' : 'Ethereum'} network`
        };
      }
    }

    // Get contract
    const contract = new ethers.Contract(contractAddress, CLAIM_CONTRACT_ABI, signer);

    // Check claimable amount
    const claimableAmount = await contract.getClaimableAmount(userAddress);
    if (claimableAmount === 0n) {
      return {
        success: false,
        error: 'No claimable amount found'
      };
    }

    console.log(`🎯 Claiming ${ethers.formatUnits(claimableAmount, 6)} USDT from contract...`);

    // Claim
    const tx = await contract.claimReward();
    console.log(`📤 Transaction sent: ${tx.hash}`);
    console.log(`⏳ Waiting for confirmation...`);
    
    const receipt = await tx.wait();
    console.log(`✅ Claimed successfully! Block: ${receipt.blockNumber}`);

    return {
      success: true,
      txHash: tx.hash
    };
  } catch (error: any) {
    console.error('❌ Claim error:', error);
    
    let errorMessage = error.message || 'Failed to claim';
    
    // User friendly error messages
    if (error.code === 'ACTION_REJECTED' || error.code === 4001) {
      errorMessage = 'Transaction rejected by user';
    } else if (error.message.includes('insufficient funds')) {
      errorMessage = 'Insufficient ETH for gas fee';
    } else if (error.message.includes('No rewards to claim')) {
      errorMessage = 'No rewards available to claim';
    }
    
    return {
      success: false,
      error: errorMessage
    };
  }
};

